import {
    DefaultFallback,
    IndexMapping,
} from 'symbols/indexMappings/store/IndexMappingsState';
import { usePortfolioSelectedSymbol } from 'portfolio/store/hooks';
import React, { useEffect, useState } from 'react';
import { SymbolInfo } from 'symbols/symbolInfo/symbolInfoSlice';
import { useSymbolsService } from 'symbols/hooks/useSymbolsService';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { mapSymbol } from 'symbols/indexMappings/indexMappingsUtils';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import SymbolInput from 'symbols/components/SymbolInput';
import { Button, Icon } from '@instinet-analytics/semantic-ui-components';
import { SymbolInfoDisplayModal } from 'symbols/symbolInfo/SymbolInfoDisplayModal';
import { IndexMappingEditorRow } from 'symbols/indexMappings/IndexMappingsEditorPanel/IndexMappingsEditorPanelState';
import './TestSymbol.scss';
import { useLogger } from 'logging/writers/hooks';

export function TestSymbol(props: {
    mappings: IndexMappingEditorRow[];
    configuredFallbackIndex?: string;
}) {
    const { configuredFallbackIndex, mappings } = props;
    const selectedSymbol = usePortfolioSelectedSymbol();
    const [input, setInput] = useState(selectedSymbol ?? '');
    const [running, setRunning] = useState(false);
    const [info, setInfo] = useState('');
    const [testedSymbol, setTestedSymbol] = useState<SymbolInfo | undefined>(
        undefined
    );
    const [result, setResult] = useState('');

    const service = useSymbolsService();
    const { symbology } = useSelectAppSettings();

    const logger = useLogger('TestSymbol');

    useEffect(() => {
        setTestedSymbol(undefined);
        setInfo('');
        setResult('');
    }, [mappings, configuredFallbackIndex]);

    const onClickTest = () => {
        setRunning(true);
        setTestedSymbol(undefined);
        setInfo(`Testing ${input}...`);
        setResult('');

        (async () => {
            try {
                const symbolInfo = await service.getSymbolInfo(
                    input,
                    symbology
                );

                if (!symbolInfo) {
                    setInfo('Invalid symbol');
                    return;
                }

                const { index: result, usedConfigFallback } = mapSymbol(
                    configuredFallbackIndex,
                    mappings as IndexMapping[],
                    symbolInfo,
                    1
                );

                setTestedSymbol(symbolInfo);
                setInfo(`Result for ${input} =`);

                if (result === DefaultFallback) {
                    const defaultInUsersSymbology = await service.convertSymbol(
                        symbolInfo.symbolIndex,
                        SymbologyValues.Instinet,
                        symbology
                    );
                    if (symbolInfo.symbolIndex) {
                        setResult(
                            `${
                                defaultInUsersSymbology ??
                                symbolInfo.symbolIndex
                            } (default)`
                        );
                    } else {
                        setResult('default = (no value)');
                    }
                } else {
                    const resultInUsersSymbology = await service.convertSymbol(
                        result,
                        SymbologyValues.Instinet,
                        symbology
                    );
                    const suffix = usedConfigFallback
                        ? ' (fallback index)'
                        : '';
                    setResult(`${resultInUsersSymbology ?? result}${suffix}`);
                }
            } catch (err) {
                logger.warn(`Error running test for ${input}`, err);
                setInfo('Error running test');
            } finally {
                setRunning(false);
            }
        })();
    };

    const disableExecute = !input || running;

    return (
        <div className="TestSymbol">
            <div className="label">Test Symbol</div>
            <SymbolInput value={input} onChange={setInput} autofocus={false} />
            <Button disabled={disableExecute} onClick={onClickTest}>
                <Icon name="lightning" />
            </Button>
            {running && <div className="ui active inline loader mini" />}
            <div className="info">
                {info} <span className="result">{result}</span>
            </div>
            <SymbolInfoDisplayModal info={testedSymbol} />
        </div>
    );
}
